import React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical, IconX } from '@tabler/icons-react';
import { cn } from '@/utils/cn';
import TeeBadge from '@/components/ui/tee-badge';
import { TeeContentThumbnail } from '@/components/ui/tee-content-thumbnail';
import { ContentMediaType } from '@/types/contentItem';

export interface TeePlaylistReorderItem {
  id: string;
  title: string;
  mediaType: ContentMediaType;
  thumbnailUrl?: string | null;
}

export interface TeePlaylistReorderListProps {
  items: TeePlaylistReorderItem[];
  onReorder: (orderedIds: string[]) => void;
  onRemove: (id: string) => void;
  onPreview?: (id: string) => void;
  disabled?: boolean;
  emptyText: string;
  dragHandleLabel: (title: string) => string;
  removeLabel: (title: string) => string;
  previewLabel?: (title: string) => string;
  mediaTypeLabel: (mediaType: ContentMediaType) => string;
  className?: string;
}

interface SortableRowProps {
  item: TeePlaylistReorderItem;
  disabled: boolean;
  onRemove: (id: string) => void;
  onPreview?: (id: string) => void;
  dragHandleLabel: (title: string) => string;
  removeLabel: (title: string) => string;
  previewLabel?: (title: string) => string;
  mediaTypeLabel: (mediaType: ContentMediaType) => string;
}

function SortableRow({
  item,
  disabled,
  onRemove,
  onPreview,
  dragHandleLabel,
  removeLabel,
  previewLabel,
  mediaTypeLabel,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-4 rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] p-4"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={dragHandleLabel(item.title)}
        title="Drag to reorder"
        disabled={disabled}
        className={cn(
          'flex min-w-[44px] min-h-[44px] items-center justify-center rounded-[6px] text-[var(--text-content-sub-title)] transition-colors hover:text-[var(--color-forest)]',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <IconGripVertical size={20} />
      </button>

      {onPreview ? (
        <button
          type="button"
          onClick={() => onPreview(item.id)}
          aria-label={previewLabel?.(item.title) ?? item.title}
          className="group relative shrink-0"
        >
          <TeeContentThumbnail
            title={item.title}
            mediaType={item.mediaType}
            thumbnailUrl={item.thumbnailUrl}
            className="h-12 w-12"
            iconSize={24}
            previewable
            fallbackLabel={mediaTypeLabel(item.mediaType)}
          />
        </button>
      ) : item.thumbnailUrl ? (
        <img
          src={item.thumbnailUrl}
          alt={item.title}
          className="h-12 w-12 shrink-0 rounded-[6px] object-cover"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[6px] bg-[var(--background-page-header)]">
          <TeeBadge label={mediaTypeLabel(item.mediaType)} size="s" type="grey" />
        </div>
      )}

      <p className="flex-1 min-w-0 truncate text-[16px] font-normal text-[var(--text-content-title)]">
        {item.title}
      </p>

      <TeeBadge label={mediaTypeLabel(item.mediaType)} size="m" type="grey" />

      <button
        type="button"
        aria-label={removeLabel(item.title)}
        title="Remove from playlist"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.id);
        }}
        className={cn(
          'flex min-w-[44px] min-h-[44px] items-center justify-center rounded-[6px] text-[var(--text-content-sub-title)] transition-colors hover:text-[var(--color-maroon)]',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <IconX size={20} />
      </button>
    </div>
  );
}

export function TeePlaylistReorderList({
  items,
  onReorder,
  onRemove,
  onPreview,
  disabled = false,
  emptyText,
  dragHandleLabel,
  removeLabel,
  previewLabel,
  mediaTypeLabel,
  className,
}: TeePlaylistReorderListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    onReorder(reordered.map((item) => item.id));
  };

  if (items.length === 0) {
    return (
      <div
        className={cn(
          'flex min-h-[160px] w-full items-center justify-center rounded-[8px] border border-[var(--border-default)] p-4',
          className
        )}
      >
        <p className="text-[16px] font-normal text-[var(--text-content-sub-title)]">
          {emptyText}
        </p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <SortableRow
              key={item.id}
              item={item}
              disabled={disabled}
              onRemove={onRemove}
              onPreview={onPreview}
              dragHandleLabel={dragHandleLabel}
              removeLabel={removeLabel}
              previewLabel={previewLabel}
              mediaTypeLabel={mediaTypeLabel}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

TeePlaylistReorderList.displayName = 'TeePlaylistReorderList';

export default TeePlaylistReorderList;
